const DEFAULT_ALLOWED_TAGS = new Set([
  "a",
  "br",
  "em",
  "li",
  "ol",
  "p",
  "s",
  "strong",
  "u",
  "ul",
])

/** Drop these entirely (including their text) instead of unwrapping. */
const REMOVED_TAGS = new Set(["script", "style", "iframe", "object", "embed"])

const isDangerousAttr = (name: string, value: string) =>
  name.startsWith("on") ||
  (name === "href" && value.trim().toLowerCase().startsWith("javascript:"))

const stripDangerousAttrs = (element: Element) => {
  for (const { name, value } of Array.from(element.attributes)) {
    if (isDangerousAttr(name, value)) element.removeAttribute(name)
  }
}

export type SanitizeHtmlOptions = {
  /** Tag names kept as elements; others are unwrapped to their children. */
  allowedTags?: ReadonlySet<string>
}

/**
 * Strips disallowed tags and dangerous attributes from HTML for safe display.
 * Uses the DOMParser API (browser / jsdom).
 */
export function sanitizeHtml(
  dirty: string,
  options: SanitizeHtmlOptions = {}
): string {
  const allowedTags = options.allowedTags ?? DEFAULT_ALLOWED_TAGS
  const doc = new DOMParser().parseFromString(dirty, "text/html")

  for (const element of Array.from(doc.body.querySelectorAll("*")).reverse()) {
    const tag = element.tagName.toLowerCase()
    if (REMOVED_TAGS.has(tag)) {
      element.remove()
    } else if (!allowedTags.has(tag)) {
      element.replaceWith(...Array.from(element.childNodes))
    } else {
      stripDangerousAttrs(element)
    }
  }

  return doc.body.innerHTML
}

/** True when the string looks like it contains an HTML tag. */
export function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value)
}
